import { FC, useDeferredValue, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { InputRef } from 'antd';
import { Avatar, Space, Tag, Typography } from 'antd';
import { TbBriefcase, TbCashBanknote, TbCircleCheck, TbSlash, TbUsers, TbUserScan } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSlashSearchFocus } from '@/hooks/useSlashSearchFocus';
import {
    createFilterableColumn,
    type FilterableColumn,
    type ListFilterRule,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import { WorkspaceResourceFilterControls } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/WorkspaceResourceFiltersRow';
import { companyExecutiveQueryOptions, type CompanyExecutive } from '@/queries';
import {
    EmptySection,
    FinancialValue,
    VirtualizedList as List,
    MiddleDot,
    SearchFilterInput,
    SkeletonList,
} from '@/shared';
import { ListViewColumnDataType } from '@/types';

import ExecutiveDetailDrawer, { type ExecutiveCompanyContext } from './ExecutiveDetailDrawer';
import { filterCompanyExecutives } from './utils';

export interface ExecutiveInsidersViewProps extends ExecutiveCompanyContext {
    emptyDescription?: string;
}

const compensationValue = (executive: CompanyExecutive) => {
    const value = executive.compensation?.total ?? executive.pay;

    if (value === undefined) {
        return undefined;
    }

    const requestedCurrency = executive.compensation?.currency ?? executive.currencyPay;
    const currency = /^[A-Z]{3}$/.test(requestedCurrency?.toLocaleUpperCase() ?? '')
        ? requestedCurrency?.toLocaleUpperCase()
        : 'USD';

    return { currency, value };
};

const ExecutiveFiltersToolbar: FC<{
    executives: CompanyExecutive[];
    filters: ListFilterRule[];
    onFiltersChange: (filters: ListFilterRule[]) => void;
    onQueryChange: (query: string) => void;
    query: string;
}> = ({ executives, filters, onFiltersChange, onQueryChange, query }) => {
    const { token } = useAntDesignTheme();
    const searchInputRef = useRef<InputRef>(null);
    useSlashSearchFocus(searchInputRef, true, { priority: 10 });
    const genderOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    executives
                        .map((executive) => executive.gender)
                        .filter((gender): gender is string => Boolean(gender)),
                ),
            )
                .sort((left, right) => left.localeCompare(right))
                .map((gender) => ({ label: gender, value: gender })),
        [executives],
    );
    const columns = useMemo<FilterableColumn[]>(
        () => [
            createFilterableColumn({
                dataType: ListViewColumnDataType.SHORT_TEXT,
                icon: <TbBriefcase style={{ color: token.colorTextTertiary }} />,
                key: 'title',
                label: 'Role',
                order: 0,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.SELECT,
                filterOperators: ['equals', 'notEquals'],
                filterOptions: [
                    { label: 'Active', value: true },
                    { label: 'Inactive', value: false },
                ],
                icon: <TbCircleCheck style={{ color: token.colorTextTertiary }} />,
                key: 'active',
                label: 'Status',
                order: 1,
            }),
            createFilterableColumn({
                allowMultipleValues: true,
                dataType: ListViewColumnDataType.SELECT,
                filterOperators: ['equals', 'notEquals'],
                filterOptions: genderOptions,
                icon: <TbUsers style={{ color: token.colorTextTertiary }} />,
                key: 'gender',
                label: 'Gender',
                order: 2,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.YEAR,
                icon: <TbBriefcase style={{ color: token.colorTextTertiary }} />,
                key: 'titleSince',
                label: 'In Role Since',
                order: 3,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.CURRENCY,
                icon: <TbCashBanknote style={{ color: token.colorTextTertiary }} />,
                key: 'totalCompensation',
                label: 'Total Compensation',
                order: 4,
            }),
        ],
        [genderOptions, token.colorTextTertiary],
    );

    return (
        <div style={{ backgroundColor: token.colorBgContainer, flex: 'none' }}>
            <div style={{ borderBottom: `1px solid ${token.colorBorder}`, padding: '0.5rem 0.75rem' }}>
                <SearchFilterInput
                    ref={searchInputRef}
                    ariaLabel='Search insiders'
                    bordered
                    placeholder='Search Insiders...'
                    size='small'
                    suffix={<TbSlash size='1rem' style={{ color: token.colorTextQuaternary }} />}
                    value={query}
                    setValue={onQueryChange}
                />
            </div>
            <WorkspaceResourceFilterControls
                columns={columns}
                docsLabel='insider filters'
                emptyColumnsDescription='Insider filter fields are unavailable.'
                emptyHint='Filter insiders by role, status, tenure, gender, or compensation.'
                filterableResourceLabel='Insiders'
                filters={filters}
                horizontalPadding='0.75rem'
                onChange={(nextFilters) => onFiltersChange(nextFilters ?? [])}
                resourceLabel='the insider roster'
            />
        </div>
    );
};

const ExecutiveInsidersView: FC<ExecutiveInsidersViewProps> = ({
    emptyDescription = 'No executive roster records are available for this company.',
    logoUrl,
    name,
    symbol,
    website,
}) => {
    const { token } = useAntDesignTheme();
    const [filters, setFilters] = useState<ListFilterRule[]>([]);
    const [query, setQuery] = useState('');
    const [selectedExecutive, setSelectedExecutive] = useState<CompanyExecutive>();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const deferredQuery = useDeferredValue(query);
    const {
        data: executives = [],
        isError,
        isLoading,
        refetch,
    } = useQuery({
        ...companyExecutiveQueryOptions.roster(symbol ?? ''),
        enabled: Boolean(symbol),
    });
    const filteredExecutives = useMemo(
        () => filterCompanyExecutives(executives, deferredQuery, filters),
        [deferredQuery, executives, filters],
    );
    const openExecutive = (executive: CompanyExecutive) => {
        setSelectedExecutive(executive);
        setDrawerOpen(true);
    };

    return (
        <section style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <ExecutiveFiltersToolbar
                executives={executives}
                filters={filters}
                query={query}
                onFiltersChange={setFilters}
                onQueryChange={setQuery}
            />
            {isLoading ? (
                <SkeletonList fade listLength={32} minHeight='32rem' />
            ) : isError ? (
                <EmptySection
                    description='The enriched executive roster could not be loaded. Try the request again.'
                    extra={<Typography.Link onClick={() => void refetch()}>Retry</Typography.Link>}
                    height='32rem'
                    icon={<TbBriefcase />}
                    label='Unable to Load Insiders'
                />
            ) : filteredExecutives.length ? (
                <List
                    dataSource={filteredExecutives}
                    style={{ flex: 1, minHeight: 0, overflow: 'auto' }}
                    renderItem={(executive: CompanyExecutive) => {
                        const compensation = compensationValue(executive);

                        return (
                            <List.Item key={executive.id} style={{ paddingInline: '0.75rem' }}>
                                <List.Item.Meta
                                    avatar={
                                        <Avatar
                                            icon={<TbUserScan />}
                                            shape='square'
                                            size='large'
                                            src={executive.avatarUrl}
                                            style={{ border: `1px solid ${token.colorBorder}`, marginTop: '0.25rem' }}
                                        />
                                    }
                                    description={
                                        <Space size={0} split={<MiddleDot horizontalMargin='0.5rem' />} wrap>
                                            {executive.title && <span>{executive.title}</span>}
                                            {executive.titleSince && (
                                                <span>In role since {executive.titleSince.toLocaleString()}</span>
                                            )}
                                            {executive.active !== undefined && (
                                                <Tag bordered={false} color={executive.active ? 'success' : 'default'}>
                                                    {executive.active ? 'Active' : 'Inactive'}
                                                </Tag>
                                            )}
                                        </Space>
                                    }
                                    title={
                                        <Typography.Link onClick={() => openExecutive(executive)}>
                                            {executive.name}
                                        </Typography.Link>
                                    }
                                />
                                {compensation && (
                                    <div style={{ textAlign: 'right' }}>
                                        <Typography.Text>
                                            <FinancialValue
                                                currency={compensation.currency}
                                                value={compensation.value}
                                            />
                                        </Typography.Text>
                                        <div>
                                            <Typography.Text type='secondary' style={{ fontSize: token.fontSizeSM }}>
                                                Total compensation
                                            </Typography.Text>
                                        </div>
                                    </div>
                                )}
                            </List.Item>
                        );
                    }}
                />
            ) : (
                <EmptySection
                    description={
                        query || filters.length
                            ? 'No executives match the current search and filter criteria.'
                            : emptyDescription
                    }
                    height='32rem'
                    icon={<TbBriefcase />}
                    label={query || filters.length ? 'No Matching Insiders' : 'No Insiders Found'}
                />
            )}
            <ExecutiveDetailDrawer
                company={{ logoUrl, name, symbol, website }}
                executive={selectedExecutive}
                executives={filteredExecutives}
                open={drawerOpen}
                onExecutiveChange={setSelectedExecutive}
                onOpenChange={setDrawerOpen}
            />
        </section>
    );
};

export default ExecutiveInsidersView;
